import { useState, useEffect, useRef } from "react";
import { UI, FONT } from "../constants/tokens";
import logo256 from "../assets/suricata-logo-256.png";

// ─── The loading screen ──────────────────────────────────────────────────────
// The app's own mark — the same tile the header wears, on the same cream plate
// with the same 22% corner — breathing slowly and fading up. Not a drawing of
// a meerkat: one mark, everywhere, is what makes it a mark.
//
// ─── The two thresholds, and why there are two ───────────────────────────────
// A splash screen that appears and disappears is worse than no splash screen:
// the eye registers that something happened and cannot tell what. So:
//
//   DELAY_MS (350ms)  nothing is drawn. A boot that resolves inside this
//                     window never shows a loading screen at all — which is
//                     most of them, now that the shell no longer waits for
//                     data (App.js, 2026-09-23).
//   MIN_VISIBLE (900) once it HAS appeared it stays at least this long and
//                     then fades out over 300ms. It cannot flash, because
//                     the shortest life it can have is a fifth of a second
//                     of fade-in, 900ms of stillness, and a fade-out.
//
// The component keeps itself on screen for the tail: the parent may swap it
// out the instant the session resolves, so `onDone` is not what ends it —
// the exit is owned here, and the fade runs after the app behind it is ready.
//
// prefers-reduced-motion: the mark is static, and both fades collapse to
// nothing (index.css). It is still shown, still for the same minimum time.

const DELAY_MS    = 350;
const MIN_VISIBLE = 900;
const FADE_MS     = 300;

export default function Splash({ leaving = false }) {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShown(true), DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  return (
    <div style={{
      minHeight: "100dvh", background: UI.surface.page,
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 20,
      fontFamily: FONT.body,
    }}>
      {(shown || leaving) && (
        <div className={leaving ? "si-splash-out" : "si-splash-in"} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 18 }}>
          <img
            className="si-splash-mark"
            src={logo256}
            alt=""
            width={112}
            height={112}
            style={{
              width: 112, height: 112, borderRadius: "22%", display: "block",
              background: UI.logoPlate, objectFit: "cover", boxShadow: UI.shadow.logo,
            }}
          />
          <span dir="ltr" style={{ color: UI.accent.base, fontWeight: 700, fontSize: 26, letterSpacing: ".5px", fontFamily: FONT.latin, unicodeBidi: "isolate", lineHeight: 1 }}>
            Suricata
          </span>
        </div>
      )}
    </div>
  );
}

// ─── The gate ────────────────────────────────────────────────────────────────
// Owns the question "is the splash on screen", so the answer does not change
// the instant the thing it was waiting for finishes. Three states:
//
//   off      nothing rendered
//   hold     the work is still running, OR it finished but the mark has not
//            yet had its 900ms
//   leaving  the mark is fading out; still rendered, for FADE_MS
//
// The important branch is the short one: if the work finished before the mark
// was ever drawn (under 350ms), the gate goes straight to `off` and nobody
// sees a flash of anything. That is the case the phone was hitting.
export function useSplashGate(active) {
  const [state, setState] = useState(() => (active ? "hold" : "off"));
  const startedAt = useRef(active ? Date.now() : 0);

  useEffect(() => {
    if (active) {
      if (!startedAt.current) startedAt.current = Date.now();
      setState("hold");
      return;
    }
    if (!startedAt.current) { setState("off"); return; }
    const elapsed = Date.now() - startedAt.current;
    if (elapsed < DELAY_MS) {          // never became visible
      startedAt.current = 0;
      setState("off");
      return;
    }
    let outTimer;
    const holdTimer = setTimeout(() => {
      setState("leaving");
      outTimer = setTimeout(() => { startedAt.current = 0; setState("off"); }, FADE_MS);
    }, Math.max(0, DELAY_MS + MIN_VISIBLE - elapsed));
    return () => { clearTimeout(holdTimer); clearTimeout(outTimer); };
  }, [active]);

  return state;
}

export const SPLASH_TIMING = { DELAY_MS, MIN_VISIBLE, FADE_MS };
